import {
  faThumbtack,
  faFilm,
  faAlignLeft,
  faStar,
  faUsers,
  faPeopleGroup,
  faPhotoFilm,
  faFolderOpen,
  faTrophy,
  faHandshake,
  faUserClock,
} from '@fortawesome/free-solid-svg-icons';

// Single source of truth for EPK section order and metadata.
// EpkViewPage, EditNavBar, the scroll-spy, and ReorderSectionsControl all read from here.

// Sections that always render first and cannot be reordered.
export const PINNED_SECTIONS = ['cover'];

// Default (and canonical) order of the sections a user can reorder.
// Supporters leads: it renders directly under the social actions row, which is
// the least intrusive spot for it, and it is short. Owners can still move it.
export const REORDERABLE_SECTIONS = [
  'supporters',
  'details',
  'synopsis',
  'uniqueness',
  'casts',
  'crews',
  'media',
  'resources',
  'buzz',
  'waitlist',
];

// id -> { labelKey, icon }. labelKey matches the existing t() keys already used
// in EditNavBar so no relabeling is needed.
export const SECTION_META = {
  cover: { labelKey: 'Cover & Banners', icon: faThumbtack },
  details: { labelKey: 'Production Details', icon: faFilm },
  synopsis: { labelKey: 'Synopsis', icon: faAlignLeft },
  uniqueness: { labelKey: 'Uniqueness', icon: faStar },
  casts: { labelKey: 'Casts', icon: faUsers },
  crews: { labelKey: 'Crews', icon: faPeopleGroup },
  media: { labelKey: 'Media', icon: faPhotoFilm },
  resources: { labelKey: 'Resources', icon: faFolderOpen },
  buzz: { labelKey: 'Buzz', icon: faTrophy },
  supporters: { labelKey: 'Supporters', icon: faHandshake },
  waitlist: { labelKey: 'Waitlist', icon: faUserClock },
};

/**
 * Resolve the effective render order for the reorderable sections.
 * - Starts from storedOrder, filtered to known reorderable ids (drops
 *   unknown/stale ids and de-dupes).
 * - Slots in any known reorderable id missing from storedOrder at its
 *   canonical position, so nothing is ever dropped (forward-compatible with
 *   sections added in a later release).
 *
 * Missing ids are placed, not appended. An EPK that saved its order before a
 * section existed never expressed an opinion about that section, so it belongs
 * where the design puts it: supporters under the social actions, not stranded at
 * the bottom of whatever that EPK happened to save. An id the owner did place
 * stays exactly where they put it.
 *
 * resolveOrder(undefined) === REORDERABLE_SECTIONS.
 */
export function resolveOrder(storedOrder) {
  const seen = new Set();
  const filtered = Array.isArray(storedOrder)
    ? storedOrder.filter((id) => {
        if (!REORDERABLE_SECTIONS.includes(id) || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
    : [];

  if (filtered.length === 0) return [...REORDERABLE_SECTIONS];

  const canonicalIndex = (id) => REORDERABLE_SECTIONS.indexOf(id);
  const resolved = [...filtered];

  for (const id of REORDERABLE_SECTIONS) {
    if (seen.has(id)) continue;
    // Sit ahead of the first section that follows this one canonically. A
    // custom order is not canonically sorted, so this is a best effort at
    // "where it belongs" rather than an exact slot; for supporters, which leads
    // the canonical list, it resolves to the front.
    const at = resolved.findIndex((existing) => canonicalIndex(existing) > canonicalIndex(id));
    resolved.splice(at === -1 ? resolved.length : at, 0, id);
  }

  return resolved;
}
